import { Settings, Info } from 'lucide-react'
import { useState } from 'react'

import { useActiveEmployees } from '@/hooks'
import { cn } from '@/lib/utils'

import type { DevelopmentConfig, InitializeWorkflowInput } from '@/hooks'

interface WorkflowConfigFormProps {
  value: InitializeWorkflowInput
  onChange: (value: InitializeWorkflowInput) => void
  disabled?: boolean
}

const DEVELOPMENT_CONFIG_OPTIONS: { value: DevelopmentConfig; label: string; description: string }[] = [
  { value: 'none', label: '无开发阶段', description: '跳过开发阶段，直接进入代码审查' },
  { value: 'frontend_only', label: '仅前端', description: '只需要前端开发' },
  { value: 'backend_only', label: '仅后端', description: '只需要后端开发' },
  { value: 'both', label: '前端+后端', description: '前端和后端并行开发，各自独立审查后汇合' },
]

/**
 * 工作流配置表单
 * 用于任务创建时配置工作流选项
 * Requirements: 1.2, 1.3, 6.2
 */
export function WorkflowConfigForm({ value, onChange, disabled }: WorkflowConfigFormProps) {
  const { data: employees } = useActiveEmployees()
  const [expanded, setExpanded] = useState(false)

  const handleChange = (updates: Partial<InitializeWorkflowInput>) => {
    onChange({ ...value, ...updates })
  }

  const handleParticipantToggle = (employeeId: string) => {
    const current = value.discussionParticipants || []
    const updated = current.includes(employeeId)
      ? current.filter((id) => id !== employeeId)
      : [...current, employeeId]
    handleChange({ discussionParticipants: updated })
  }

  return (
    <div className="rounded-lg border bg-card">
      {/* Header */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-muted/50"
        disabled={disabled}
      >
        <div className="flex items-center gap-2">
          <Settings className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">工作流配置</span>
        </div>
        <span className="text-xs text-muted-foreground">
          {expanded ? '收起' : '展开'}
        </span>
      </button>

      {/* Content */}
      {expanded && (
        <div className="border-t px-4 py-4 space-y-6">
          {/* 提示信息 */}
          <div className="flex items-start gap-2 rounded-md bg-blue-50 p-3 text-sm text-blue-700 dark:bg-blue-900/20 dark:text-blue-400">
            <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <p>配置任务的工作流阶段，可选阶段可以跳过，必需阶段（代码审查、测试、产品验收）不可跳过。</p>
          </div>

          {/* 产品研讨 */}
          <div className="space-y-2">
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={value.productDiscussionEnabled ?? false}
                onChange={(e) => handleChange({ productDiscussionEnabled: e.target.checked })}
                disabled={disabled}
                className="h-4 w-4 rounded border-gray-300"
              />
              <div>
                <span className="text-sm font-medium">启用产品研讨</span>
                <p className="text-xs text-muted-foreground">
                  任务开始前进行产品研讨，可邀请相关人员参与
                </p>
              </div>
            </label>

            {/* 研讨参与人员选择 */}
            {value.productDiscussionEnabled && (
              <div className="ml-7 mt-3 space-y-2">
                <label className="text-sm font-medium">研讨参与人员</label>
                <div className="max-h-40 overflow-y-auto rounded-md border p-2">
                  {employees && employees.length > 0 ? (
                    <div className="space-y-1">
                      {employees.map((emp) => (
                        <label
                          key={emp.id}
                          className="flex items-center gap-2 rounded px-2 py-1 hover:bg-muted cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={value.discussionParticipants?.includes(emp.id) ?? false}
                            onChange={() => handleParticipantToggle(emp.id)}
                            disabled={disabled}
                            className="h-3.5 w-3.5 rounded border-gray-300"
                          />
                          <span className="text-sm">{emp.name}</span>
                          <span className="text-xs text-muted-foreground">{emp.email}</span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground p-2">暂无可选员工</p>
                  )}
                </div>
                {value.discussionParticipants && value.discussionParticipants.length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    已选择 {value.discussionParticipants.length} 人
                  </p>
                )}
              </div>
            )}
          </div>

          {/* UI设计 */}
          <div className="space-y-2">
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={value.uiDesignEnabled ?? false}
                onChange={(e) => handleChange({ uiDesignEnabled: e.target.checked })}
                disabled={disabled}
                className="h-4 w-4 rounded border-gray-300"
              />
              <div>
                <span className="text-sm font-medium">启用UI设计</span>
                <p className="text-xs text-muted-foreground">
                  包含UI设计和产品确认两个阶段
                </p>
              </div>
            </label>
          </div>

          {/* 运维部署 */}
          <div className="space-y-2">
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={value.opsDeploymentEnabled ?? false}
                onChange={(e) => handleChange({ opsDeploymentEnabled: e.target.checked })}
                disabled={disabled}
                className="h-4 w-4 rounded border-gray-300"
              />
              <div>
                <span className="text-sm font-medium">启用运维部署</span>
                <p className="text-xs text-muted-foreground">
                  代码审查通过后需运维部署才能进入测试阶段
                </p>
              </div>
            </label>
          </div>

          {/* 开发配置 */}
          <div className="space-y-3">
            <label className="text-sm font-medium">开发配置</label>
            <div className="grid gap-2">
              {DEVELOPMENT_CONFIG_OPTIONS.map((option) => (
                <label
                  key={option.value}
                  className={cn(
                    'flex items-start gap-3 rounded-md border p-3 cursor-pointer transition-colors',
                    value.developmentConfig === option.value
                      ? 'border-primary bg-primary/5'
                      : 'hover:bg-muted/50',
                    disabled && 'cursor-not-allowed opacity-50'
                  )}
                >
                  <input
                    type="radio"
                    name="developmentConfig"
                    value={option.value}
                    checked={value.developmentConfig === option.value}
                    onChange={() => handleChange({ developmentConfig: option.value })}
                    disabled={disabled}
                    className="mt-0.5 h-4 w-4"
                  />
                  <div>
                    <span className="text-sm font-medium">{option.label}</span>
                    <p className="text-xs text-muted-foreground">{option.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* 工作流预览 */}
          <div className="space-y-2">
            <label className="text-sm font-medium">工作流预览</label>
            <div className="flex flex-wrap items-center gap-1 rounded-md bg-muted/50 p-3">
              {getWorkflowPreview(value).map((stage, index, arr) => (
                <span key={stage} className="flex items-center">
                  <span className="rounded bg-background px-2 py-0.5 text-xs">{stage}</span>
                  {index < arr.length - 1 && (
                    <span className="mx-1 text-muted-foreground">→</span>
                  )}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * 获取工作流预览
 */
function getWorkflowPreview(config: InitializeWorkflowInput): string[] {
  const stages: string[] = []

  if (config.productDiscussionEnabled) {
    stages.push('产品研讨')
  }

  if (config.uiDesignEnabled) {
    stages.push('UI设计', 'UI确认')
  }

  if (config.developmentConfig === 'frontend_only') {
    stages.push('前端开发')
  } else if (config.developmentConfig === 'backend_only') {
    stages.push('后端开发')
  } else if (config.developmentConfig === 'both') {
    stages.push('前端开发 ∥ 后端开发') // 并行符号
  }

  stages.push('代码审查')

  if (config.opsDeploymentEnabled) {
    stages.push('运维部署')
  }

  stages.push('测试', '产品验收')

  return stages
}
